const money = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  const hasDecimals = !Number.isInteger(n);
  return `$${n.toLocaleString("es-MX", { minimumFractionDigits: hasDecimals ? 2 : 0, maximumFractionDigits: 2 })}`;
};

export const PRICE_LEVEL_RANGES = Object.freeze([
  { value: 1, label: "Hasta $80" },
  { value: 2, label: "$80 – $160" },
  { value: 3, label: "$160 – $240" },
  { value: 4, label: "$240 – $320" },
  { value: 5, label: "$320 – $400" },
  { value: 6, label: "$400 – $480" },
  { value: 7, label: "$480+" },
]);

// Fuente: Google Sheet histórico "VENAQQI Registros Venaqqi Puebla 2025".
// Son gastos reales registrados en visitas, NO costos por persona: el número de
// comensales de esas visitas aún debe confirmarse antes de normalizarlos.
export const LEGACY_VISIT_COSTS_MXN = Object.freeze({
  1: 120,
  2: 160,
  3: 110,
  4: 250,
  5: 200,
  6: 125,
  7: 351.90,
  8: 249,
  9: 220,
  10: 95,
  14: 300,
  15: 180,
  16: 35,
  17: 180,
  18: 139,
  19: 120,
  20: 90,
  21: 160,
  22: 130,
});

// Lugares hardcodeados sin costo respaldado por el Sheet histórico.
// Sus etiquetas editoriales legacy no deben presentarse como costo real.
export const LEGACY_COST_UNVERIFIED_PLACE_IDS = Object.freeze([11, 12, 13, 23]);

export const formatReportedPriceLevel = value => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const level = Math.min(Math.max(Math.round(n), 1), 7);
  const range = PRICE_LEVEL_RANGES.find(item => item.value === level)?.label;
  if (!range) return null;
  return `${"$".repeat(Math.min(level, 5))} · ${range}`;
};

export const formatVerifiedPlaceCost = place => {
  if (!place?.priceVerifiedAt && !place?.price_verified_at) return null;
  const min = money(place?.priceMinMxn ?? place?.price_min_mxn);
  const max = money(place?.priceMaxMxn ?? place?.price_max_mxn);
  if (!min && !max) return null;
  if (min && max) return min === max ? `${min} MXN` : `${min}–${max} MXN`;
  if (min) return `Desde ${min} MXN`;
  return `Hasta ${max} MXN`;
};

const isPublishedReview = review => !review?.pending && !review?.metadata?.rejected;
const isEditorialReview = review => String(review?.userid || review?.userId || "").toLowerCase() === "venaqqi";

const publishedReviews = reviews => (Array.isArray(reviews) ? reviews : []).filter(isPublishedReview);
const communityReviews = reviews => publishedReviews(reviews).filter(review => !isEditorialReview(review));

const structuredPerPersonSpend = reviews => {
  const values = communityReviews(reviews).map(review => {
    const directCost = Number(review?.cost_per_person ?? review?.costPerPerson);
    if (Number.isFinite(directCost) && directCost > 0) return directCost;

    const metadata = review?.metadata || {};
    const total = Number(review?.total_bill ?? review?.totalBill ?? metadata.total_spend_mxn ?? metadata.totalSpendMxn);
    const partySize = Number(review?.party_size ?? review?.partySize ?? metadata.party_size ?? metadata.partySize);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(partySize) || partySize <= 0) return null;
    return total / partySize;
  }).filter(value => Number.isFinite(value) && value > 0);
  if (!values.length) return null;
  const average = values.reduce((total, value) => total + value, 0) / values.length;
  const formatted = money(average);
  return formatted ? `${formatted} MXN por persona` : null;
};

const reportedPriceRange = reviews => {
  // Los niveles editoriales legacy no pertenecen necesariamente a la escala
  // actual 1–7. Solo las reseñas de usuarios alimentan este rango.
  const levels = communityReviews(reviews)
    .map(review => Number(review?.precio))
    .filter(value => Number.isFinite(value) && value > 0);
  if (!levels.length) return null;
  const average = levels.reduce((total, value) => total + value, 0) / levels.length;
  return formatReportedPriceLevel(average);
};

const historicalVisitCost = place => {
  const id = Number(place?.id);
  if (!Number.isFinite(id)) return null;
  const amount = LEGACY_VISIT_COSTS_MXN[id];
  if (!Number.isFinite(amount)) return null;
  const formatted = money(amount);
  return formatted ? `${formatted} MXN` : null;
};

export const resolvePlaceCost = (place, reviews = []) => {
  const verified = formatVerifiedPlaceCost(place);
  if (verified) {
    return { kind: "verified", label: verified, caption: "Precio verificado" };
  }

  const perPerson = structuredPerPersonSpend(reviews);
  if (perPerson) {
    return {
      kind: "reported_per_person",
      label: perPerson,
      caption: "Costo por persona reportado por usuarios",
    };
  }

  const reported = reportedPriceRange(reviews);
  if (reported) {
    return {
      kind: "reported_range",
      label: reported,
      caption: "Rango por persona reportado en reseñas",
    };
  }

  const historicalCost = historicalVisitCost(place);
  if (historicalCost) {
    return {
      kind: "historical_visit",
      label: historicalCost,
      caption: "Gasto registrado en visita 2025 · comensales por confirmar",
    };
  }

  const placeId = Number(place?.id);
  const legacyCostUnverified = LEGACY_COST_UNVERIFIED_PLACE_IDS.includes(placeId);
  const historical = publishedReviews(reviews).find(review =>
    isEditorialReview(review)
    && !review?.metadata?.historical_cost_invalid
    && String(review?.precio_label || "").trim()
  );
  if (historical && !legacyCostUnverified) {
    return {
      kind: "editorial_reference",
      label: historical.precio_label,
      caption: "Referencia histórica Venaqqi",
    };
  }

  return { kind: "unverified", label: "Precio por verificar", caption: "Sin datos de costo publicados" };
};